import React, { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Loader2, Sparkles, X } from 'lucide-react';
import { api } from '../../services/authService';

function formatApiErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg != null ? e.msg : JSON.stringify(e))).join('\n');
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
}

const EXAMPLES = [
  'Gmail or Yahoo users with a signal in the last 30 days',
  'Fast-ID score of 7 or higher, not in California',
  'Deliverable emails that clicked an Everflow offer this year',
];

/**
 * "Describe your audience" → drafts a criteria tree via POST /v2/segments/draft.
 * When the tree already has conditions the user chooses to replace it or refine it.
 */
const NlDraftBar = ({
  organizationId,
  criteria,
  hasConditions,
  onApply,
  disabled = false,
  collapsible = false,
  className = '',
}) => {
  const [text, setText] = useState('');
  const [mode, setMode] = useState('refine'); // refine | replace
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { notes, unresolved, sql_error }
  const [open, setOpen] = useState(!collapsible);
  const expanded = !collapsible || open;

  const submit = async () => {
    const prompt = text.trim();
    if (!prompt || busy || !organizationId) return;
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const body = { prompt, organization_id: parseInt(organizationId, 10) };
      if (hasConditions && mode === 'refine') body.existing_criteria = criteria;
      const res = await api.post('/api/v2/segments/draft', body);
      const data = res.data || {};
      const tree = data.criteria;
      if (!tree || !(tree.children || []).length) {
        setError(
          (data.unresolved || []).length
            ? `Nothing could be mapped to columns:\n${data.unresolved.join('\n')}`
            : 'The draft came back empty — try naming the data you mean (domain, last signal, state…).'
        );
        return;
      }
      onApply(tree);
      setResult({ notes: data.notes || '', unresolved: data.unresolved || [], sql_error: data.sql_error || '' });
      setText('');
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || 'Drafting failed.');
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div
      className={
        collapsible
          ? `rounded-xl border border-hairline bg-surface shadow-card ${className}`
          : `rounded-xl border border-hairline bg-gradient-to-br from-[#F8FAFC] to-white p-4 shadow-card ${className}`
      }
    >
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
          aria-expanded={expanded}
        >
          <span className="flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-[#0062FB]" aria-hidden />
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-400">Describe your audience</span>
            <span className="text-xs font-normal text-zinc-400">Optional</span>
          </span>
          {expanded ? <ChevronUp className="h-4 w-4 shrink-0 text-zinc-400" aria-hidden /> : <ChevronDown className="h-4 w-4 shrink-0 text-zinc-400" aria-hidden />}
        </button>
      ) : (
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
          <Sparkles className="h-3.5 w-3.5 text-[#0062FB]" aria-hidden />
          Describe your audience
        </h3>
        {hasConditions ? (
          <div className="inline-flex rounded-lg border border-hairline bg-white p-0.5 text-[11px] font-medium" role="radiogroup" aria-label="How to apply the draft">
            {[
              { v: 'refine', label: 'Refine current' },
              { v: 'replace', label: 'Start over' },
            ].map((o) => (
              <button
                key={o.v}
                type="button"
                role="radio"
                aria-checked={mode === o.v}
                onClick={() => setMode(o.v)}
                className={`rounded-md px-2 py-0.5 ${mode === o.v ? 'bg-[#E8EEF5] text-[#18181B]' : 'text-zinc-500 hover:text-[#18181B]'}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      )}

      {expanded ? (
      <div className={collapsible ? 'px-4 pb-4' : undefined}>
      <div className="flex items-start gap-2">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={onKeyDown}
          rows={2}
          disabled={disabled || busy}
          placeholder={hasConditions && mode === 'refine' ? 'e.g. also exclude anyone in Texas, and make it the last 60 days' : 'e.g. Gmail or Yahoo users with a signal in the last 30 days, aged 35–50'}
          className="min-h-[3.25rem] flex-1 resize-y rounded-lg border border-input bg-surface px-3 py-2 text-sm placeholder:text-zinc-400 focus:outline-none focus:ring-1 focus:ring-[#18181B]/10 disabled:opacity-60"
          aria-label="Describe your audience"
        />
        <button
          type="button"
          onClick={submit}
          disabled={disabled || busy || !text.trim() || !organizationId}
          className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-md border border-[#0062FB] bg-[#0062FB] px-3 text-sm font-medium text-white hover:bg-[#0052E0] disabled:opacity-50"
          title="Draft criteria (⌘/Ctrl + Enter)"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {busy ? 'Drafting…' : 'Draft'}
        </button>
      </div>

      {!hasConditions && !text && !result && !error ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => setText(ex)}
              className="rounded-full border border-hairline bg-white px-2.5 py-1 text-[11px] text-zinc-600 hover:border-zinc-300 hover:text-[#18181B]"
            >
              {ex}
            </button>
          ))}
        </div>
      ) : null}

      {error ? (
        <p className="mt-2 whitespace-pre-line text-xs text-red-600" role="alert">
          {error}
        </p>
      ) : null}

      {result ? (
        <div className="mt-3 rounded-lg border border-hairline bg-white px-3 py-2 text-xs text-zinc-700">
          <div className="flex items-start justify-between gap-2">
            <p>
              <span className="font-medium text-[#18181B]">Drafted.</span> {result.notes || 'Review the conditions below and adjust anything that is off.'}
            </p>
            <button type="button" onClick={() => setResult(null)} className="text-zinc-400 hover:text-zinc-700" aria-label="Dismiss">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          {result.unresolved.length ? (
            <ul className="mt-1.5 space-y-0.5 text-amber-700">
              {result.unresolved.map((u, i) => (
                <li key={i} className="flex items-start gap-1">
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                  <span>{u}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {result.sql_error ? <p className="mt-1.5 text-red-600">{result.sql_error}</p> : null}
        </div>
      ) : null}
      </div>
      ) : null}
    </div>
  );
};

export default NlDraftBar;
